/**
 * Mapping detail — signature + strategy info + row CRUD.
 *
 * Three sections stacked top-down (no tabs — total info is small):
 *
 *   1. Header: name, kind chip, signature-locked chip, enabled toggle,
 *      delete action.
 *   2. Signature: read-only summary table of declared inputs/outputs.
 *      Editing the signature is intentionally a future step (lands
 *      with proper "edit if not locked" UI; for now signatures are
 *      effectively frozen after creation).
 *   3. Rows: editable table — only for `manual_table` strategy.
 *      Add / inline-edit / delete. Composite of all input values is
 *      the row's natural key (backend rejects duplicates).
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Loader2, Plus, Save, Trash2, X } from "lucide-react";
import * as React from "react";
import { Link, useParams } from "react-router-dom";

import { MappingTestPanel } from "@/components/mappings/MappingTestPanel";
import { StrategyConfigEditor } from "@/components/mappings/StrategyConfigEditor";
import { Alert } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PageContainer, PageContent } from "@/components/ui/page";
import { Pagination } from "@/components/ui/pagination";
import { Table, Tbody, Td, Th, Thead, Tr } from "@/components/ui/table";
import {
  mappingsApi,
  STRATEGY_META,
  type MappingField,
  type MappingFieldType,
  type MappingRowSummary,
  type MappingSummary,
} from "@/lib/api-mappings";
import { usePaginationState } from "@/lib/pagination";
import { usePermissions } from "@/lib/permissions";

export function MappingDetailPage() {
  const { id = "" } = useParams<{ id: string }>();
  const { can } = usePermissions();
  const canWrite = can("mappings", "write");
  const qc = useQueryClient();

  const mappingQuery = useQuery({
    queryKey: ["mapping", id],
    queryFn: () => mappingsApi.get(id),
    enabled: !!id,
  });

  const toggleEnabled = useMutation({
    mutationFn: (enabled: boolean) => mappingsApi.update(id, { enabled }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["mapping", id] }),
  });

  // Bridges the strategy editor's unsaved-changes state to the test
  // panel. MUST stay above the early returns — React's hook order
  // rule means a useState called only on some renders ("more hooks
  // than during the previous render" error) is fatal.
  const [strategyDirty, setStrategyDirty] = React.useState(false);

  if (mappingQuery.isLoading) {
    return (
      <PageContainer>
        <PageContent>
          <div className="text-sm text-muted-foreground">Loading…</div>
        </PageContent>
      </PageContainer>
    );
  }
  if (mappingQuery.isError || !mappingQuery.data) {
    return (
      <PageContainer>
        <PageContent>
          <Alert tone="danger">Mapping not found.</Alert>
        </PageContent>
      </PageContainer>
    );
  }

  const m = mappingQuery.data;
  const strategyMeta = STRATEGY_META[m.strategy_kind];

  return (
    <PageContainer>
      <div className="flex items-center justify-between gap-3 border-b border-border bg-surface px-4 py-2">
        <div className="flex min-w-0 items-center gap-3">
          <Link
            to="/mappings"
            className="text-muted-foreground hover:text-accent"
            aria-label="Back to mappings"
            title="Back to mappings"
          >
            <ArrowLeft className="h-4 w-4" strokeWidth={1.5} />
          </Link>
          <div className="truncate text-sm font-semibold" title={m.name}>
            {m.name}
          </div>
          <Badge tone="neutral" className="text-[10px] uppercase tracking-wide">
            {strategyMeta.label}
          </Badge>
          {m.signature_locked && (
            <Badge
              tone="warning"
              className="text-[10px]"
              title="A workflow uses this mapping. The signature is locked: only nullable-output additions are allowed."
            >
              signature locked
            </Badge>
          )}
          {!m.enabled && (
            <Badge tone="danger" className="text-[10px]">disabled</Badge>
          )}
        </div>
        {canWrite && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => toggleEnabled.mutate(!m.enabled)}
            // Enable refuses on incomplete config (backend enforces;
            // hint at it here for the UX).
            disabled={
              toggleEnabled.isPending || (!m.enabled && !m.config_complete)
            }
            title={
              !m.enabled && !m.config_complete
                ? "Set the strategy config first — see the editor below."
                : m.enabled
                ? "Disable this mapping"
                : "Enable this mapping"
            }
          >
            {toggleEnabled.isPending && <Loader2 className="h-3 w-3 animate-spin" />}
            {m.enabled ? "Disable" : "Enable"}
          </Button>
        )}
      </div>

      <PageContent>
        {m.description && (
          <p className="mb-4 text-sm text-muted-foreground">{m.description}</p>
        )}

        {!strategyMeta.available && (
          <Alert tone="info" className="mb-4">
            <strong>{strategyMeta.label} resolver isn't implemented yet.</strong>
            <p className="mt-1 text-xs">{strategyMeta.description}</p>
          </Alert>
        )}

        {/* "Configure to use" banner — db_lookup / http_lookup land
            here on first create with empty config and ``enabled=False``
            until the strategy editor below is filled in. */}
        {!m.config_complete && m.strategy_kind !== "manual_table" && (
          <Alert tone="warning" className="mb-4">
            <strong>This mapping isn't configured yet.</strong>
            <p className="mt-1 text-xs">
              Fill in the {strategyMeta.label} fields below and save —
              the mapping will become enableable. Until then, calls to
              <code className="ml-1 font-mono">/resolve</code> (and the
              future workflow node) will return an error.
            </p>
          </Alert>
        )}

        {/* Signature read-only summary */}
        <section className="mb-6">
          <h2 className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Signature
          </h2>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <FieldList title="Inputs" fields={m.inputs} />
            <FieldList title="Outputs" fields={m.outputs} />
          </div>
        </section>

        {/* Strategy config editor — different form per strategy
            (manual_table is just a "no config" placeholder). */}
        <div className="mb-6">
          <StrategyConfigEditor
            mapping={m}
            canEdit={canWrite}
            onDirtyChange={setStrategyDirty}
          />
        </div>

        {/* Quick test — runs the same /resolve code path the future
            call_mapping workflow node will use. Hidden when the
            config is incomplete (resolver would crash). Ignores the
            enabled toggle on purpose so operators validate before
            going live. */}
        <div className="mb-6">
          <MappingTestPanel mapping={m} strategyDirty={strategyDirty} />
        </div>

        {/* Rows — only for manual_table */}
        {m.strategy_kind === "manual_table" && (
          <ManualTableRows mapping={m} canEdit={canWrite} />
        )}
      </PageContent>
    </PageContainer>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Signature display
// ──────────────────────────────────────────────────────────────────────

function FieldList({ title, fields }: { title: string; fields: MappingField[] }) {
  return (
    <div className="rounded border border-border/60">
      <div className="border-b border-border/60 bg-muted/40 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
        {title} ({fields.length})
      </div>
      <ul className="divide-y divide-border/60">
        {fields.map((f) => (
          <li key={f.name} className="flex items-baseline gap-3 px-3 py-1.5 text-sm">
            <span className="font-mono text-xs font-medium">{f.name}</span>
            <span className="font-mono text-[10px] text-muted-foreground">{f.type}</span>
            {!f.required && (
              <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                nullable
              </span>
            )}
            {f.description && (
              <span className="ml-auto truncate text-[11px] text-muted-foreground">
                {f.description}
              </span>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Rows editor (manual_table strategy)
// ──────────────────────────────────────────────────────────────────────

interface ManualTableRowsProps {
  mapping: MappingSummary;
  canEdit: boolean;
}

function ManualTableRows({ mapping, canEdit }: ManualTableRowsProps) {
  const qc = useQueryClient();
  const { page, pageSize, setPage, setPageSize } = usePaginationState();
  const [error, setError] = React.useState<string | null>(null);
  const [editing, setEditing] = React.useState<RowDraft | null>(null);

  const rowsQuery = useQuery({
    queryKey: ["mapping-rows", mapping.id, page, pageSize],
    queryFn: () =>
      mappingsApi.listRows(mapping.id, { page, page_size: pageSize }),
  });

  const createMutation = useMutation({
    mutationFn: (draft: RowDraft) =>
      mappingsApi.createRow(mapping.id, {
        inputs: castRowValues(draft.inputs, mapping.inputs),
        outputs: castRowValues(draft.outputs, mapping.outputs),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["mapping-rows", mapping.id] });
      qc.invalidateQueries({ queryKey: ["mapping", mapping.id] });
      setEditing(null);
      setError(null);
    },
    onError: (err: Error) => setError(err.message),
  });

  const updateMutation = useMutation({
    mutationFn: (draft: RowDraft & { id: string }) =>
      mappingsApi.updateRow(mapping.id, draft.id, {
        inputs: castRowValues(draft.inputs, mapping.inputs),
        outputs: castRowValues(draft.outputs, mapping.outputs),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["mapping-rows", mapping.id] });
      setEditing(null);
      setError(null);
    },
    onError: (err: Error) => setError(err.message),
  });

  const deleteMutation = useMutation({
    mutationFn: (rowId: string) => mappingsApi.deleteRow(mapping.id, rowId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["mapping-rows", mapping.id] });
      qc.invalidateQueries({ queryKey: ["mapping", mapping.id] });
    },
  });

  const rows = rowsQuery.data?.items ?? [];
  const total = rowsQuery.data?.total ?? 0;

  return (
    <section>
      <div className="mb-2 flex items-center justify-between">
        <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          Rows ({total})
        </h2>
        {canEdit && (
          <Button
            size="sm"
            onClick={() =>
              setEditing({
                inputs: emptyRowDict(mapping.inputs),
                outputs: emptyRowDict(mapping.outputs),
              })
            }
            disabled={editing !== null}
          >
            <Plus className="h-3.5 w-3.5" /> Add row
          </Button>
        )}
      </div>

      {error && (
        <Alert tone="danger" className="mb-3">
          {error}
        </Alert>
      )}

      <div className="rounded-lg overflow-hidden border border-border/60">
        <Table>
          <Thead>
            <Tr>
              {mapping.inputs.map((f) => (
                <Th key={`in-${f.name}`} className="text-left">
                  <span className="font-mono text-xs">{f.name}</span>
                  <span className="ml-1 text-[10px] text-muted-foreground">in</span>
                </Th>
              ))}
              {mapping.outputs.map((f) => (
                <Th key={`out-${f.name}`} className="text-left">
                  <span className="font-mono text-xs">{f.name}</span>
                  <span className="ml-1 text-[10px] text-muted-foreground">out</span>
                </Th>
              ))}
              {canEdit && <Th className="w-20 text-right" />}
            </Tr>
          </Thead>
          <Tbody>
            {editing && !("id" in editing) && (
              <RowEditor
                key="__new"
                mapping={mapping}
                draft={editing}
                onChange={setEditing}
                onSave={() => createMutation.mutate(editing)}
                onCancel={() => {
                  setEditing(null);
                  setError(null);
                }}
                pending={createMutation.isPending}
              />
            )}
            {rows.map((r) =>
              editing && "id" in editing && editing.id === r.id ? (
                <RowEditor
                  key={r.id}
                  mapping={mapping}
                  draft={editing}
                  onChange={setEditing}
                  onSave={() =>
                    updateMutation.mutate(editing as RowDraft & { id: string })
                  }
                  onCancel={() => {
                    setEditing(null);
                    setError(null);
                  }}
                  pending={updateMutation.isPending}
                />
              ) : (
                <Tr key={r.id}>
                  {mapping.inputs.map((f) => (
                    <Td key={`in-${f.name}`} className="font-mono text-xs">
                      {formatCell(r.inputs[f.name])}
                    </Td>
                  ))}
                  {mapping.outputs.map((f) => (
                    <Td key={`out-${f.name}`} className="font-mono text-xs">
                      {formatCell(r.outputs[f.name])}
                    </Td>
                  ))}
                  {canEdit && (
                    <Td className="text-right">
                      <div className="inline-flex items-center gap-1">
                        <button
                          type="button"
                          onClick={() =>
                            setEditing(rowToDraft(r, mapping))
                          }
                          disabled={editing !== null}
                          className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-30"
                          title="Edit row"
                        >
                          <Save className="h-3.5 w-3.5" />
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            if (window.confirm("Delete this row?")) {
                              deleteMutation.mutate(r.id);
                            }
                          }}
                          disabled={deleteMutation.isPending || editing !== null}
                          className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-danger disabled:opacity-30"
                          title="Delete row"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </Td>
                  )}
                </Tr>
              ),
            )}
            {rows.length === 0 && !editing && (
              <Tr>
                <Td
                  colSpan={
                    mapping.inputs.length +
                    mapping.outputs.length +
                    (canEdit ? 1 : 0)
                  }
                  className="text-center text-xs text-muted-foreground py-6"
                >
                  No rows yet. Click "Add row" to insert the first.
                </Td>
              </Tr>
            )}
          </Tbody>
        </Table>
        <Pagination
          page={page}
          pageSize={pageSize}
          total={total}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
        />
      </div>
    </section>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Inline row editor — one form per row
// ──────────────────────────────────────────────────────────────────────

type RowDraft = {
  id?: string;
  inputs:  Record<string, string>;  // raw string form for editing
  outputs: Record<string, string>;
};

function RowEditor({
  mapping,
  draft,
  onChange,
  onSave,
  onCancel,
  pending,
}: {
  mapping: MappingSummary;
  draft: RowDraft;
  onChange: (d: RowDraft) => void;
  onSave: () => void;
  onCancel: () => void;
  pending: boolean;
}) {
  function setVal(side: "inputs" | "outputs", name: string, value: string) {
    onChange({
      ...draft,
      [side]: { ...draft[side], [name]: value },
    });
  }
  return (
    <Tr className="bg-muted/30">
      {mapping.inputs.map((f) => (
        <Td key={`in-${f.name}`}>
          <Input
            value={draft.inputs[f.name] ?? ""}
            onChange={(e) => setVal("inputs", f.name, e.target.value)}
            placeholder={f.type}
            className="h-7 font-mono text-xs"
          />
        </Td>
      ))}
      {mapping.outputs.map((f) => (
        <Td key={`out-${f.name}`}>
          <Input
            value={draft.outputs[f.name] ?? ""}
            onChange={(e) => setVal("outputs", f.name, e.target.value)}
            placeholder={f.type + (f.required ? "" : " (nullable)")}
            className="h-7 font-mono text-xs"
          />
        </Td>
      ))}
      <Td className="text-right">
        <div className="inline-flex items-center gap-1">
          <button
            type="button"
            onClick={onSave}
            disabled={pending}
            className="rounded p-1 text-success hover:bg-success/10 disabled:opacity-30"
            title="Save row"
          >
            {pending ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Save className="h-3.5 w-3.5" />
            )}
          </button>
          <button
            type="button"
            onClick={onCancel}
            disabled={pending}
            className="rounded p-1 text-muted-foreground hover:bg-muted disabled:opacity-30"
            title="Cancel"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      </Td>
    </Tr>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Cell value helpers
// ──────────────────────────────────────────────────────────────────────

function emptyRowDict(fields: MappingField[]): Record<string, string> {
  return Object.fromEntries(fields.map((f) => [f.name, ""]));
}

function rowToDraft(
  r: MappingRowSummary,
  mapping: MappingSummary,
): RowDraft {
  return {
    id: r.id,
    inputs: Object.fromEntries(
      mapping.inputs.map((f) => [f.name, stringifyValue(r.inputs[f.name])]),
    ),
    outputs: Object.fromEntries(
      mapping.outputs.map((f) => [f.name, stringifyValue(r.outputs[f.name])]),
    ),
  };
}

function stringifyValue(v: unknown): string {
  if (v === null || v === undefined) return "";
  if (typeof v === "boolean") return v ? "true" : "false";
  return String(v);
}

/** Convert the editor's raw string values into typed JSON the backend
 *  signature validator expects. Empty strings for nullable fields are
 *  dropped (backend treats missing keys as null). */
function castRowValues(
  raw: Record<string, string>,
  fields: MappingField[],
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const f of fields) {
    const val = raw[f.name];
    if (val === undefined || val === "") {
      // Empty input on a non-required field → omit (server treats as null).
      // On a required field, let the server reject so the error is explicit.
      continue;
    }
    out[f.name] = castOne(val, f.type);
  }
  return out;
}

function castOne(v: string, t: MappingFieldType): unknown {
  switch (t) {
    case "string":
      return v;
    case "integer":
      return parseInt(v, 10);
    case "number":
      return Number(v);
    case "boolean":
      return v.toLowerCase() === "true" || v === "1";
  }
}

function formatCell(v: unknown): React.ReactNode {
  if (v === null || v === undefined) {
    return <span className="text-muted-foreground italic">—</span>;
  }
  if (typeof v === "boolean") return v ? "true" : "false";
  return String(v);
}
